import { useState } from "react";
import { UserX, UserCog, Undo2 } from "lucide-react";
import AdminPortalShell from "@/components/admin/AdminPortalShell";
import ImpersonationBanner from "@/components/admin/ImpersonationBanner";
import DisableUserModal from "@/components/admin/_modals/DisableUserModal";
import ImpersonateWarningModal from "@/components/admin/_modals/ImpersonateWarningModal";
import RefundModal from "@/components/admin/_modals/RefundModal";
import {
  ADMIN_USERS,
  CHARGES_BY_USER,
} from "@/components/admin/_mockData";

/**
 * /admin/_showcase — preview surface for the destructive-action modals
 * + impersonation banner. Lets the design be reviewed without triggering
 * any real WorkOS / Stripe calls. Removed once the modals are wired into
 * the user-detail page in ROADS-476 deliverables A2, A5, C2.
 */
type ModalKey = null | "disable" | "impersonate" | "refund";

export default function AdminDesignShowcasePage() {
  const [modal, setModal] = useState<ModalKey>(null);
  const targetUser = ADMIN_USERS[2];
  const altUser = ADMIN_USERS[1];
  const charge = CHARGES_BY_USER[altUser.id][0];

  return (
    <AdminPortalShell
      title="Design showcase"
      subtitle="Preview the destructive-action modals + impersonation banner without triggering live calls."
      testId="admin-design-showcase-page"
    >
      <section className="space-y-3">
        <Heading>Impersonation banner</Heading>
        <p className="text-xs text-slate-500">
          Live state during an active impersonation. Renders at the top of
          every admin + product page; pinned, not dismissable except via the
          Exit button.
        </p>
        <div className="overflow-hidden rounded-xl border border-slate-200">
          <ImpersonationBanner
            targetEmail={targetUser.email}
            minutesRemaining={27}
            operatorName="Bharat C."
          />
        </div>
      </section>
      <section className="mt-8 space-y-3">
        <Heading>Critical-action modals</Heading>
        <p className="text-xs text-slate-500">
          Click a button to open the modal. All actions are mocked — Cancel
          to close.
        </p>
        <div className="flex flex-wrap gap-2.5">
          <ShowcaseButton
            icon={UserX}
            label="Open Disable user modal"
            onClick={() => setModal("disable")}
          />
          <ShowcaseButton
            icon={UserCog}
            label="Open Impersonate warning modal"
            onClick={() => setModal("impersonate")}
          />
          <ShowcaseButton
            icon={Undo2}
            label="Open Refund modal"
            onClick={() => setModal("refund")}
          />
        </div>
      </section>
      <DisableUserModal
        user={altUser}
        open={modal === "disable"}
        onCancel={() => setModal(null)}
      />
      <ImpersonateWarningModal
        user={targetUser}
        open={modal === "impersonate"}
        onCancel={() => setModal(null)}
      />
      <RefundModal
        charge={charge}
        open={modal === "refund"}
        onCancel={() => setModal(null)}
      />
    </AdminPortalShell>
  );
}

function Heading({ children }: { children: string }) {
  return (
    <h2 className="text-[10px] font-bold uppercase tracking-widest text-slate-500">
      {children}
    </h2>
  );
}

function ShowcaseButton({
  icon: Icon,
  label,
  onClick,
}: {
  icon: typeof UserX;
  label: string;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="flex items-center gap-2 rounded-lg border border-slate-300 bg-white px-4 py-2.5 text-sm font-semibold text-slate-700 hover:bg-slate-50"
    >
      <Icon aria-hidden="true" className="w-3.5 h-3.5" />
      {label}
    </button>
  );
}
